import uniqid from 'uniqid';
import bsUtils from 'bootstrap/js/src/util';
import keyCodes from '@utils/key-codes';
import isFunction from '@utils/is-function';
import forceAsync from '@utils/force-async';
import FocusTrap from '@utils/focus-trap';
import { screenLgMax } from '@utils/match-media';
import { megaNavActive } from './mega-nav';

const $body = $('body');

const CLASS_NAME_OPEN = '-open';
const CLASS_NAME_NOSCROLL = 'noscroll';

const EVENT_KEY  = '_mobileNav_';
const EVENT_CLICK = `click.${EVENT_KEY}`;
const EVENT_KEYDOWN = `keydown.${EVENT_KEY}`;

const FOCUS_TRAP_OPTIONS = {
    nextKeys: [ keyCodes.ARROW_RIGHT ],
    prevKeys: [ keyCodes.ARROW_LEFT ]
};

export default class MobileMegaNav {
    #isOpen = false;
    #focusTrap = null;

    constructor({ $el }) {
        this.$el = $el;

        this.$toggle = this.$el.find('[data-control="mobileToggle"]');
        this.$close = this.$el.find('[data-control="mobileClose"]');

        this.id = uniqid.time('megaMenu_');
        this.isOpen = false;
        this.originEl = null;

        this.init();
    }

    get isOpen() {
        return this.#isOpen;
    }

    set isOpen(val) {
        this.#isOpen = val;

        if(val) {
            this.$el.addClass('d-block');

            // force DOM to reflow so next class added will trigger animation
            bsUtils.reflow(this.$el.get(0));
            this.$el.addClass(CLASS_NAME_OPEN);
            $body.addClass(CLASS_NAME_NOSCROLL);
            this.#focusTrap = new FocusTrap(this.$el, FOCUS_TRAP_OPTIONS);

            if ($('.hospital-header.header').length) {
                $('.hospital-header.header').css({'position':'relative', 'z-index':'unset'});
            }
            
            // force async to prevent possible issues with click event propagating right after this is set
            forceAsync(() => {
                $body.on(EVENT_CLICK, evt => this.onBodyClick(evt));
            });
        } else {
            this.$el.removeClass(CLASS_NAME_OPEN);
            $body.removeClass(CLASS_NAME_NOSCROLL);
            $body.off(EVENT_CLICK);
            this.#focusTrap = this.#focusTrap?.destroy();

            if ($('.hospital-header.header').length) {
                $('.hospital-header.header').css({'position':'sticky', 'z-index':'15'});
            }

            // wait until transition is finished to set focus and fully hide element
            this.afterTransition(() => {
                this.$el.removeClass('d-block');
                if(this.originEl) {
                    this.originEl.focus();
                    this.originEl = null;
                }
            });
        }

        this.$toggle.attr('aria-expanded', val);
    }

    afterTransition(callback) {
        if(isFunction(callback)) {
            const duration = bsUtils.getTransitionDurationFromElement(this.$el.get(0));
            setTimeout(callback, duration);
        }
    }

    addToggle($toggle) {
        this.$toggle.off(EVENT_CLICK);
        this.$toggle.push.apply(this.$toggle, $toggle);

        this.$toggle.on(EVENT_CLICK, evt => this.open(evt.currentTarget));
        this.setupAccessibility();
    }

    close() {
        this.isOpen = false;
    }

    init() {
        this.setupListeners();
        this.setupAccessibility();
    }

    onBodyClick(evt) {
        const $origin = $(evt.target);
        if(!$origin.closest(this.$el).length) {
            this.close();
        }
    }

    onBodyKeydown(evt) {
        const key = evt.which ? evt.which : evt.keyCode;
        if(key === keyCodes.ESCAPE && this.isOpen && !megaNavActive()) {
            this.close();
        }
    }

    /** only activate body level functionality while on a mobile device  */
    onScreenChange(evt) {
        if(this.isOpen) {
            if(evt.matches) {
                this.$el.addClass(CLASS_NAME_OPEN);
                $body.addClass(CLASS_NAME_NOSCROLL);
                $body.on(EVENT_CLICK, evt => this.onBodyClick(evt));
                $body.on(EVENT_KEYDOWN, evt => this.onBodyKeydown(evt));
            } else {
                this.$el.removeClass(CLASS_NAME_OPEN);
                $body.removeClass(CLASS_NAME_NOSCROLL);
                $body.off(EVENT_CLICK);
                $body.off(EVENT_CLICK);
            }
        }
    }

    open(originEl) {
        // store element that triggered the nav
        if(originEl) {
            this.originEl = originEl;
        }

        this.isOpen = true;
    }

    setupAccessibility() {
        this.$el.attr('id', this.id);
        this.$toggle.attr({
            'aria-expanded': this.isOpen,
            'aria-controls': this.id
        });
    }

    setupListeners() {
        this.$close.on(EVENT_CLICK, () => this.close());
        screenLgMax.addEventListener('change', evt => this.onScreenChange(evt));

        if(screenLgMax.matches) {
            $body.on(EVENT_KEYDOWN, evt => this.onBodyKeydown(evt));
        }
    }
}

let mobileMegaNav = null;
const el = document.querySelector('.js-mega-nav');
if(el) {
    mobileMegaNav = new MobileMegaNav({
        $el: $(el)
    });
    
    const $toggler = $('.js-mega-nav-toggler');
    mobileMegaNav.addToggle($toggler);
}

export {
    mobileMegaNav
};
